import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
  MsalInterceptor
} from '@azure/msal-angular';

import { errorInterceptor } from './interceptors/error.interceptor';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './factory/azure-msal';
import { apiInterceptor } from './interceptors/api.interceptor';
import { MAT_RIPPLE_GLOBAL_OPTIONS, globalRippleConfig } from './constants/api.constant';
import { provideMarkdown } from 'ngx-markdown';
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { ICON_LIST } from './constants/generated-icons';

/*
 * Initializes and registers all SVG icons from the generated icon list with Angular Material's icon registry.
 * This function runs during application initialization to make all icons available for use throughout the app.
 * Icons are registered using their filename (without .svg extension) as the icon name.
 */
const initializeSvgIcons = (): void => {
  const matIconRegistry = inject(MatIconRegistry);
  const domSanitizer = inject(DomSanitizer);

  ICON_LIST.forEach(filename => {
    const iconName = filename.replace('.svg', '');
    const url = `/assets/icons/${filename}`;
    matIconRegistry.addSvgIcon(iconName, domSanitizer.bypassSecurityTrustResourceUrl(url));
  });
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi(), withFetch(), withInterceptors([errorInterceptor, apiInterceptor])),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    provideMarkdown(),
    // Provide the single factory function directly.
    provideAppInitializer(initializeSvgIcons)
  ]
};
